import React from "react";
import { toast } from "sonner";
import { getDebugSnapshotJson, captureError } from "../utils/debugSnapshot";

/**
 * Global Error Boundary with Debug Snapshot
 * 
 * Features:
 * - Catches React errors and shows friendly fallback UI
 * - "Copy debug snapshot" button for ALL users (not just admins)
 * - Snapshot includes build info, route, device, and recent errors
 * - No tokens, secrets, or PII in snapshot
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      snapshotCopied: false,
    };
  }
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error, errorInfo) {
    console.error("[GlobalErrorBoundary] Caught error:", error);
    console.error("[GlobalErrorBoundary] Component stack:", errorInfo.componentStack);
    
    // Capture error for debug snapshot
    captureError(error?.message || String(error), {
      stack: error?.stack ? error.stack.slice(0, 2000) : '',
      componentStack: errorInfo?.componentStack?.slice(0, 1000) || '',
    });
    
    this.setState({ errorInfo });
  }

  copySnapshot = async () => {
    try {
      const snapshot = getDebugSnapshotJson({ includeErrors: true });
      
      // Add the specific error that triggered this boundary
      if (this.state.error) {
        snapshot.crashError = {
          message: this.state.error?.message || String(this.state.error),
          stack: this.state.error?.stack?.slice(0, 2000) || '',
        };
      }
      
      const text = JSON.stringify(snapshot, null, 2);

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        this.setState({ snapshotCopied: true });
        toast.success("Debug snapshot copied to clipboard");
        
        // Reset copied state after 2s
        setTimeout(() => this.setState({ snapshotCopied: false }), 2000);
        return;
      }

      // Clipboard fallback for older browsers
      window.prompt("Copy this debug snapshot and send to support:", text);
    } catch (e) {
      console.error("[ErrorBoundary] Snapshot copy failed:", e);
      toast.error("Could not copy snapshot");
    }
  };

  render() {
    if (this.state.hasError) {
      const { snapshotCopied, error } = this.state;
      
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 px-4">
          <div className="max-w-lg w-full text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full border-2 border-rose-500/40 flex items-center justify-center mb-2">
              <span className="text-3xl">⚠️</span>
            </div>
            
            <h1 className="text-2xl font-semibold">Something went wrong</h1>
            
            <p className="text-slate-300 text-sm">
              Try refreshing the page. If the problem persists, copy the debug snapshot 
              and send it to support — it helps us fix the issue faster.
            </p>
            
            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center rounded-xl 
                           bg-rose-600 hover:bg-rose-500 px-5 py-2.5 text-sm font-semibold 
                           text-white shadow-sm transition-colors"
                data-testid="error-refresh-btn"
              >
                Refresh Page
              </button>
              
              <button
                type="button"
                onClick={() => (window.location.href = "/dashboard")}
                className="inline-flex items-center justify-center rounded-xl 
                           border border-slate-600 px-5 py-2.5 text-sm font-medium 
                           text-slate-200 hover:bg-slate-800 transition-colors"
                data-testid="error-dashboard-btn"
              >
                Go to Dashboard
              </button>
              
              <button
                type="button"
                onClick={this.copySnapshot}
                className={`inline-flex items-center justify-center rounded-xl 
                           border px-5 py-2.5 text-sm font-medium transition-colors
                           ${snapshotCopied 
                             ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' 
                             : 'border-slate-600 text-slate-200 hover:bg-slate-800'
                           }`}
                data-testid="error-snapshot-btn"
              >
                {snapshotCopied ? '✓ Copied!' : 'Copy debug snapshot'}
              </button>
            </div>
            
            {/* Error details (collapsible) */}
            {error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-slate-400 hover:text-slate-300">
                  Error details (for developers)
                </summary>
                <pre className="mt-3 max-h-48 overflow-auto rounded-xl border border-slate-700 
                               bg-slate-900/50 p-3 text-xs text-slate-300 whitespace-pre-wrap">
                  {String(error?.stack || error)}
                </pre>
              </details>
            )}
            
            {/* Contact support link */}
            <div className="pt-4 text-sm text-slate-500">
              Still stuck?{' '}
              <a 
                href="/contact" 
                className="text-rose-400 hover:text-rose-300 underline"
              >
                Contact support
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
